import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import styled, { css } from 'styled-components';
import {
  Button,
  Buttons,
  CancelButton,
  DeleteConfirmation,
  Dropdown,
  Icon,
  ModalCard,
  SingleSelect,
} from '~/components';
import { useApi, useConfirmation, useToast, useWorkspace } from '~/contexts';
import { useLocalStorage } from '~/hooks';
import { colors, weights } from '~/styles';
import Assistant from './Assistant';
import {
  defaultPanelWidth,
  PANEL_MIN_WIDTH,
  DRAWER_WIDTH,
  TRANSITION_MS,
  TRANSITION,
  useAssistant,
} from './AssistantContext';
import ModelSelector, { useModelSelector } from './ModelSelector';
import Sidebar from './Sidebar';

const SIDEBAR_EXPANDED_WIDTH = '10.3125rem';
const SIDEBAR_COLLAPSED_WIDTH = '3.125rem';
const DRAG_MIN = PANEL_MIN_WIDTH;
const HEADER_HEIGHT = '3.125rem';
// Ideal expanded width: chat max-width (820) + drag handle area (12) + sidebar collapsed (48) + borders (2)
const CHAT_EXPANDED_WIDTH = 882;

const S = {
  Panel: styled.div`
    position: fixed;
    top: ${({ $hasMessage }) => ($hasMessage ? `calc(2.5rem + ${HEADER_HEIGHT})` : HEADER_HEIGHT)};
    bottom: 0;
    display: flex;
    flex-direction: column;
    background: ${colors.white};
    border-left: 1px solid ${colors.grey10};
    z-index: ${({ $zIndex }) => $zIndex};
    transition: ${({ $dragging }) => ($dragging ? 'none' : `right ${TRANSITION}, width ${TRANSITION}, top ${TRANSITION}`)};

    ${({ $mode, $open, $sidebarExpanded, $widthOverride, $collapsing, $dragging }) => {
      if ($mode === 'drawer' && !$collapsing) {
        const sidebarWidth = $sidebarExpanded ? SIDEBAR_EXPANDED_WIDTH : SIDEBAR_COLLAPSED_WIDTH;
        const drawerWidth = `clamp(${DRAWER_WIDTH}px, 75vw, 1600px)`;
        return css`
          width: ${drawerWidth};
          max-width: calc(100vw - ${sidebarWidth});
          right: ${$open ? '0' : `calc(-1 * ${drawerWidth})`};
          top: 0;
        `;
      }
      if ($widthOverride) {
        return css`
          width: ${$widthOverride}px;
          max-width: 75vw;
          right: ${$open ? '0' : `-${$widthOverride}px`};
          ${!$dragging && `transition: right ${TRANSITION}, width ${TRANSITION}, top ${TRANSITION};`}
        `;
      }
      const panelWidth = `max(${PANEL_MIN_WIDTH}px, 25vw)`;
      return css`
        width: ${panelWidth};
        max-width: 75vw;
        right: ${$open ? '0' : `calc(-1 * ${panelWidth})`};
      `;
    }}
  `,

  PanelHeader: styled.div`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0 0.75rem;
    height: ${HEADER_HEIGHT};
    flex-shrink: 0;

    ${({ $noBorder }) =>
      !$noBorder &&
      css`
        border-bottom: 1px solid ${colors.grey10};
      `}
  `,

  HeaderLeft: styled.div`
    display: flex;
    align-items: center;
    gap: 0.125rem;
  `,

  HeaderActions: styled.div`
    display: flex;
    align-items: center;
    gap: 0.125rem;
    margin-left: auto;
  `,

  ModeButton: styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 2.125rem;
    height: 2.125rem;
    min-width: 2.125rem;
    padding: 0;
    border: none;
    border-radius: 0.25rem;
    background: transparent;
    color: ${({ $active }) => ($active ? colors.primary : colors.grey75)};
    cursor: pointer;
    font-size: 0.875rem;

    &:hover {
      background: ${({ $active }) => ($active ? 'transparent' : colors.grey5)};
      color: ${({ $active }) => ($active ? colors.primary : 'inherit')};
    }
  `,

  PanelBody: styled.div`
    flex: 1;
    display: flex;
    flex-direction: row;
    overflow: hidden;
    position: relative;
    ${({ $mode }) => $mode === 'panel' && 'padding-left: 0.75rem;'}
  `,

  ChatColumn: styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  `,

  CloseButton: styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 2.125rem;
    height: 2.125rem;
    border: none;
    border-radius: 0.25rem;
    background: transparent;
    color: ${colors.danger};
    cursor: pointer;
    font-size: 0.875rem;
    padding: 0;

    &:hover {
      background: ${colors.grey5};
      color: ${colors.danger};
    }
  `,

  DragHandle: styled.div`
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    width: 12px;
    cursor: col-resize;
    z-index: 75;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${colors.grey5};
    border-left: 1px solid ${colors.grey10};
    border-right: 1px solid ${colors.grey10};
    transition: background 0.15s ease, border-color 0.15s ease;

    &::after {
      content: '⋮';
      font-size: 16px;
      color: ${colors.grey40};
      transition: color 0.15s ease;
    }

    &:hover,
    &:active {
      background: ${colors.primary5};
      border-color: ${colors.primary25};

      &::after {
        color: ${colors.primary};
      }
    }
  `,

  DrawerOverlay: styled.div`
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 89;
    opacity: ${({ $visible }) => ($visible ? 1 : 0)};
    transition: opacity ${TRANSITION};
    pointer-events: ${({ $visible }) => ($visible ? 'auto' : 'none')};
  `,

  SettingsDropdown: styled(Dropdown)`
    .settings-menu {
      min-width: 9.5625rem;
      right: -3.75rem;
    }

    .settings-menu > div {
      border-radius: 0.5rem;
      box-shadow: 0 0 0.875rem rgba(0, 0, 0, 0.25);
      padding: 0.875rem 0;
      margin-top: 0.25rem;
      position: relative;
      overflow: visible;

      &::before {
        content: '';
        position: absolute;
        top: -0.375rem;
        left: 50%;
        transform: translateX(-50%);
        width: 1.3125rem;
        height: 0.8125rem;
        background: ${colors.white};
        clip-path: polygon(50% 0%, 0% 100%, 100% 100%);
        filter: drop-shadow(0 -2px 2px rgba(0, 0, 0, 0.08));
      }
    }

    .settings-menu .dropdown-item {
      font-size: 0.75rem;
      padding: 0.375rem 1.25rem;
    }
  `,

  NewChatButton: styled.button`
    display: flex;
    align-items: center;
    gap: 0.25rem;
    background: transparent;
    border: none;
    padding: 0.375rem 0.5rem;
    margin: 0;
    cursor: pointer;
    font-size: 0.875rem;
    font-weight: ${weights.bold};
    color: ${colors.grey75};
    white-space: nowrap;
    height: auto;
    border-radius: 0.25rem;

    &:hover:not(:disabled) {
      background: ${colors.grey5};
      color: ${colors.grey75};
    }

    &:disabled {
      color: ${colors.grey25};
      background: transparent;
      cursor: default;
    }
  `,

  MenuItem: styled(Dropdown.Item)`
    font-size: 0.75rem;

    .icon {
      font-size: 0.75rem;
    }
  `,

  DeleteMenuItem: styled(Dropdown.Item)`
    font-size: 0.75rem;
    color: ${colors.danger};

    &:hover {
      color: ${colors.danger};
    }
  `,
};

export default function AssistantPanel({ sidebarExpanded }) {
  const api = useApi();
  const { workspace, hasMessage } = useWorkspace();
  const confirmation = useConfirmation();
  const toast = useToast();
  const {
    isOpen,
    mode,
    setMode,
    closePanel,
    panelWidthOverride,
    setPanelWidthOverride,
    setForcePanelWidth,
    isDragging: isDraggingContext,
    setIsDragging,
    sidebarOpen,
    setSidebarOpen,
    toggleSidebar,
    profiles,
    profileId,
    setProfileId,
  } = useAssistant();

  const [model, setModel] = useModelSelector();

  // Lifted conversation state
  const [conversations, setConversations] = useState({ data: [], isReady: false, error: null });
  const conversationStorage = useLocalStorage({ key: 'assistant_conversation_id' });
  const [conversationId, setConversationIdRaw] = useState(() => conversationStorage.get() || null);

  const setConversationId = useCallback(
    (id) => {
      setConversationIdRaw(id);
      conversationStorage.set(id || '');
    },
    [conversationStorage],
  );

  // Assistant profile modal
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [pendingProfileId, setPendingProfileId] = useState(profileId);

  const fetchConversations = useCallback(async () => {
    const { data } = await api.www.workspaces(workspace.id).ai().conversations().list();
    setConversations({ data, isReady: true, error: null });
    return data;
  }, [api, workspace.id]);

  const conversationIdRef = useRef(conversationId);
  conversationIdRef.current = conversationId;

  useEffect(() => {
    if (!isOpen) return;
    fetchConversations().then((data) => {
      // If the stored conversation no longer exists, clear it
      if (conversationIdRef.current && !data?.some((c) => c.id === conversationIdRef.current)) {
        setConversationId(null);
      }
    });
  }, [isOpen, fetchConversations, setConversationId]);

  const handleNewChat = useCallback(() => {
    setConversationId(null);
    if (mode === 'panel') setSidebarOpen(false);
  }, [mode, setSidebarOpen, setConversationId]);

  const handleSelectConversation = useCallback(
    (id) => {
      setConversationId(id);
      if (mode === 'panel') setSidebarOpen(false);
    },
    [mode, setSidebarOpen, setConversationId],
  );

  const handleRenameConversation = useCallback(
    async (id, title) => {
      await api.www.workspaces(workspace.id).ai().conversations(id).rename({ title });
      setConversations((prev) => ({
        ...prev,
        data: prev.data.map((c) => (c.id === id ? { ...c, title } : c)),
      }));
    },
    [api, workspace.id],
  );

  const handleDeleteConversation = useCallback(
    async (id) => {
      await confirmation.prompt((resolve) => (
        <DeleteConfirmation
          resolve={async (confirm) => {
            if (!confirm) {
              resolve(false);
              return;
            }
            await api.www.workspaces(workspace.id).ai().conversations(id).delete();
            setConversations((prev) => ({
              ...prev,
              data: prev.data.filter((c) => c.id !== id),
            }));
            if (conversationId === id) {
              setConversationId(null);
            }
            resolve(true);
            toast.success('Conversation deleted.');
          }}>
          Are you sure you want to delete this conversation?
        </DeleteConfirmation>
      ));
    },
    [api, workspace.id, conversationId, confirmation, toast, setConversationId],
  );

  // Drag-to-resize
  const isDragging = useRef(false);
  const startX = useRef(0);
  const startWidth = useRef(0);

  const handleMouseDown = useCallback(
    (e) => {
      if (mode !== 'panel') return;
      e.preventDefault();
      isDragging.current = true;
      setIsDragging(true);
      startX.current = e.clientX;
      startWidth.current = panelWidthOverride || defaultPanelWidth();

      const handleMouseMove = (moveEvent) => {
        if (!isDragging.current) return;
        const delta = startX.current - moveEvent.clientX;
        const dragMax = Math.floor(window.innerWidth * 0.75);
        const newWidth = Math.min(dragMax, Math.max(DRAG_MIN, startWidth.current + delta));
        setPanelWidthOverride(newWidth);
      };

      const handleMouseUp = () => {
        isDragging.current = false;
        setIsDragging(false);
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      };

      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    },
    [mode, panelWidthOverride, setPanelWidthOverride, setIsDragging],
  );

  const panelRef = useRef(null);

  const handleDoubleClick = useCallback(() => {
    if (mode !== 'panel') return;
    const dragMax = Math.floor(window.innerWidth * 0.75);
    const quarter = Math.min(dragMax, Math.max(DRAG_MIN, Math.floor(window.innerWidth * 0.25)));
    const expanded = Math.min(dragMax, Math.max(DRAG_MIN, CHAT_EXPANDED_WIDTH));
    const current = panelWidthOverride || defaultPanelWidth();
    const target = Math.abs(current - expanded) < Math.abs(current - quarter) ? quarter : expanded;

    const el = panelRef.current;
    if (el) {
      el.style.transition = `width ${TRANSITION}, right ${TRANSITION}`;
      const onEnd = () => {
        el.style.transition = '';
        el.removeEventListener('transitionend', onEnd);
      };
      el.addEventListener('transitionend', onEnd);
    }
    setPanelWidthOverride(target);
  }, [mode, panelWidthOverride, setPanelWidthOverride]);

  const isDrawer = mode === 'drawer';

  // Track whether the panel has ever been opened (lazy-mount Assistant)
  const [everOpened, setEverOpened] = useState(isOpen);

  // Keep content visible during close animation, then hide (but stay mounted)
  const [contentVisible, setContentVisible] = useState(isOpen);
  const prevIsOpen = useRef(isOpen);
  useEffect(() => {
    if (isOpen) {
      setEverOpened(true);
      setContentVisible(true);
    } else if (prevIsOpen.current && !isOpen) {
      // Closing — keep content visible until slide-out finishes
      const timer = setTimeout(() => setContentVisible(false), TRANSITION_MS);
      prevIsOpen.current = isOpen;
      return () => clearTimeout(timer);
    }
    prevIsOpen.current = isOpen;
  }, [isOpen]);

  // z-index: keep drawer z-index during collapse animation, drop after transition
  const [panelZIndex, setPanelZIndex] = useState(isDrawer ? 90 : 55);

  // Sidebar slide during mode transitions (drawer mode only)
  const [sidebarSlideOut, setSidebarSlideOut] = useState(false);
  const [collapsing, setCollapsing] = useState(false);

  // Animate drawer overlay fade in/out
  const [showOverlay, setShowOverlay] = useState(isDrawer && isOpen);
  const [overlayVisible, setOverlayVisible] = useState(isDrawer && isOpen);

  const prevOverlayActive = useRef(isDrawer && isOpen);
  const isInitialMount = useRef(true);
  useEffect(() => {
    const shouldBeActive = isDrawer && isOpen && !collapsing;

    if (shouldBeActive) {
      prevOverlayActive.current = true;
      setShowOverlay(true);
      if (isInitialMount.current) {
        // Already visible on mount — no fade needed
        isInitialMount.current = false;
        setOverlayVisible(true);
        return;
      }
      // Mount the overlay at opacity 0, then fade in after paint
      setOverlayVisible(false);
      const t = setTimeout(() => setOverlayVisible(true), 20);
      return () => clearTimeout(t);
    } else if (prevOverlayActive.current) {
      // Was active, now closing or collapsing — fade out then unmount
      prevOverlayActive.current = false;
      setOverlayVisible(false);
      const timer = setTimeout(() => {
        setShowOverlay(false);
      }, TRANSITION_MS);
      return () => clearTimeout(timer);
    }
  }, [isDrawer, isOpen, collapsing]);

  return (
    <>
      {/* Drawer overlay */}
      {showOverlay &&
        ReactDOM.createPortal(<S.DrawerOverlay $visible={overlayVisible} onClick={closePanel} />, document.body)}

      <S.Panel
        ref={panelRef}
        data-testid="assistant-panel"
        $open={isOpen}
        $mode={mode}
        $sidebarExpanded={sidebarExpanded}
        $hasMessage={hasMessage}
        $widthOverride={panelWidthOverride}
        $zIndex={panelZIndex}
        $collapsing={collapsing}
        $dragging={isDraggingContext}>
        {/* Drag handle — only in panel mode */}
        {mode === 'panel' && <S.DragHandle onMouseDown={handleMouseDown} onDoubleClick={handleDoubleClick} />}

        <S.PanelBody $mode={mode}>
          {contentVisible && (
            <Sidebar
              mode={mode}
              conversations={conversations.data}
              activeConversationId={conversationId}
              onSelectConversation={handleSelectConversation}
              onNewChat={handleNewChat}
              onRenameConversation={handleRenameConversation}
              onDeleteConversation={handleDeleteConversation}
              sidebarOpen={sidebarOpen}
              onToggleSidebar={toggleSidebar}
              slideOut={sidebarSlideOut}
            />
          )}
          <S.ChatColumn>
            <S.PanelHeader $noBorder={isDrawer}>
              {mode === 'panel' && (
                <S.HeaderLeft>
                  <S.ModeButton onClick={toggleSidebar} title="Chat History">
                    <Icon icon="messages" type="far" />
                  </S.ModeButton>
                  <S.NewChatButton onClick={handleNewChat} disabled={!conversationId}>
                    + Start New Chat
                  </S.NewChatButton>
                </S.HeaderLeft>
              )}
              <S.HeaderActions>
                <ModelSelector model={model} onModelChange={setModel} />
                {conversationId && (
                  <S.SettingsDropdown align="right">
                    {({ setIsOpen, isOpen: dropdownOpen }) => (
                      <>
                        <Dropdown.Trigger>
                          <S.ModeButton
                            $active={dropdownOpen}
                            onClick={() => setIsOpen(!dropdownOpen)}
                            title="Settings">
                            <Icon icon="cog" type="far" />
                          </S.ModeButton>
                        </Dropdown.Trigger>
                        <Dropdown.Menu className="settings-menu">
                          {profiles.length > 1 && (
                            <S.MenuItem
                              onClick={() => {
                                setPendingProfileId(profileId);
                                setShowProfileModal(true);
                                setIsOpen(false);
                              }}>
                              Assistant Profile
                            </S.MenuItem>
                          )}
                          <S.DeleteMenuItem
                            onClick={() => {
                              handleDeleteConversation(conversationId);
                              setIsOpen(false);
                            }}>
                            Delete
                          </S.DeleteMenuItem>
                        </Dropdown.Menu>
                      </>
                    )}
                  </S.SettingsDropdown>
                )}
                <S.ModeButton
                  onClick={() => {
                    if (mode === 'drawer') {
                      // Slide out sidebar + shrink width together, stay in drawer mode until done
                      setSidebarSlideOut(true);
                      setCollapsing(true);
                      const currentWidth = panelRef.current?.offsetWidth;
                      const targetWidth = panelWidthOverride || defaultPanelWidth();
                      // Immediately push main body over
                      setForcePanelWidth(targetWidth);
                      if (currentWidth) {
                        setPanelWidthOverride(currentWidth);
                        requestAnimationFrame(() => {
                          requestAnimationFrame(() => {
                            setPanelWidthOverride(targetWidth);
                          });
                        });
                        // Switch mode only after animation completes
                        setTimeout(() => {
                          setMode('panel');
                          setPanelZIndex(55);
                          setSidebarSlideOut(false);
                          setCollapsing(false);
                          setForcePanelWidth(null);
                        }, TRANSITION_MS);
                      } else {
                        setMode('panel');
                        setPanelZIndex(55);
                        setSidebarSlideOut(false);
                        setCollapsing(false);
                        setForcePanelWidth(null);
                      }
                    } else {
                      // Expand to drawer — sidebar slides in
                      setSidebarSlideOut(true);
                      setPanelZIndex(90);
                      setMode('drawer');
                      requestAnimationFrame(() => {
                        requestAnimationFrame(() => {
                          setSidebarSlideOut(false);
                        });
                      });
                    }
                  }}
                  title={mode === 'drawer' ? 'Collapse' : 'Expand'}>
                  <Icon icon={mode === 'drawer' ? 'compress' : 'expand'} />
                </S.ModeButton>
                <S.CloseButton data-testid="assistant-panel-close" onClick={closePanel} title="Close">
                  <Icon icon="xmark" />
                </S.CloseButton>
              </S.HeaderActions>
            </S.PanelHeader>
            {everOpened && (
              <Assistant
                model={model}
                conversationId={conversationId}
                onConversationIdChange={setConversationId}
                onConversationsChange={setConversations}
                hidden={!contentVisible}
              />
            )}
          </S.ChatColumn>
        </S.PanelBody>
      </S.Panel>

      {/* Assistant profile modal */}
      {showProfileModal && (
        <ModalCard title="Assistant Profile" onClose={() => setShowProfileModal(false)}>
          <ModalCard.Body>
            <SingleSelect
              name="profile-select"
              placeholder="Assistant Profile"
              materialPlaceholder=""
              value={pendingProfileId}
              onChange={({ value }) => setPendingProfileId(value)}>
              {profiles.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </SingleSelect>
          </ModalCard.Body>
          <ModalCard.Footer>
            <Buttons align="right">
              <CancelButton onClick={() => setShowProfileModal(false)}>Cancel</CancelButton>
              <Button
                onClick={() => {
                  setProfileId(pendingProfileId);
                  setShowProfileModal(false);
                }}>
                Apply
              </Button>
            </Buttons>
          </ModalCard.Footer>
        </ModalCard>
      )}
    </>
  );
}
